import {
    addDoc,
    collection,
    CollectionReference,
    deleteDoc,
    doc,
    Firestore,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    onSnapshot,
    orderBy,
    Query,
    query,
    QueryConstraint,
    setDoc,
    where,
} from 'firebase/firestore'
import {BaseMapper, ICollectionDocument} from '@/firestore'

type WhereOperation =
    | '<'
    | '<='
    | '=='
    | '!='
    | '>='
    | '>'
    | 'array-contains'
    | 'in'
    | 'not-in'
    | 'array-contains-any'

export interface QueryBuilder {
    where(field: string, op: WhereOperation, value: unknown): QueryBuilder
    orderBy(field: string, direction: 'asc' | 'desc'): QueryBuilder
    limit(n: number): QueryBuilder
    build(): Query
}

export abstract class BaseRepository<T> {
    protected abstract collectionName: string
    protected abstract mapper: BaseMapper<T>
    protected coll: CollectionReference | undefined

    protected constructor(protected firestore: Firestore) {
    }

    protected get collectionRef(): CollectionReference {
        if (!this.coll) {
            this.coll = collection(this.firestore, this.collectionName)
        }
        return this.coll
    }

    async findById(id: string): Promise<T | null> {
        // Check if id is empty or undefined
        if (!id) {
            console.error('Error: Attempted to call findById with an empty ID')
            return null
        }
        const docRef = doc(this.collectionRef, id)
        const docSnap = await getDoc(docRef)
        return docSnap.exists() ? this.mapper.fromFirestore(docSnap) : null
    }

    async findAll(): Promise<T[]> {
        const snapshot = await getDocs(this.collectionRef)
        return snapshot.docs.map(docSnap => this.mapper.fromFirestore(docSnap))
    }

    protected async create(data: T & ICollectionDocument): Promise<T & ICollectionDocument> {
        // Create a document reference with auto-generated ID
        const docRef = doc(this.collectionRef)
        
        // Set the entity ID to match the document ID
        data.id = docRef.id

        // Convert to Firestore format after ID is set
        const dataToSave = JSON.parse(JSON.stringify(this.mapper.toFirestore(data)));
        
        // Save to Firestore using setDoc instead of addDoc to use our specific ID
        await setDoc(docRef, dataToSave)
        
        return data
    }

    protected async upsert(data: T & ICollectionDocument): Promise<T & ICollectionDocument> {
        if (!data.id) {
            // No ID - create new document with auto-generated ID
            return this.create(data)
        } else {
            // Has ID - update existing document
            const docRef = doc(this.collectionRef, data.id)
            
            // Ensure data.id matches docRef.id (should already be the case)
            data.id = docRef.id
            
            // Convert to Firestore format after ID is confirmed
            const dataToSave = JSON.parse(JSON.stringify(this.mapper.toFirestore(data)))
            
            // Save to Firestore
            await setDoc(docRef, dataToSave)
            return data
        }
    }

    async delete(id: string): Promise<void> {
        // Check if id is empty or undefined
        if (!id) {
            console.error('Error: Attempted to call findById with an empty ID')
            return
        }
        const docRef = doc(this.collectionRef, id)
        await deleteDoc(docRef)
    }

    async query(queryFn: (collection: CollectionReference, qb: QueryBuilder) => Query): Promise<T[]> {
        const qb = this.createQueryBuilder()
        const queryRef = queryFn(this.collectionRef, qb)
        const snapshot = await getDocs(queryRef)
        return snapshot.docs.map(docSnap => this.mapper.fromFirestore(docSnap))
    }

    watch(id: string): { data: T | null; unsub: () => void } {
        // Check if id is empty or undefined
        if (!id) {
            console.error('Error: Attempted to call watch with an empty ID')
            return { data: null, unsub: () => {} }
        }
        const result = { data: null as T | null }
        const docRef = doc(this.collectionRef, id)
        const unsub = onSnapshot(docRef, (snapshot) => {
            result.data = snapshot.exists() ? this.mapper.fromFirestore(snapshot) : null
        })
        return { ...result, unsub }
    }

    watchQuery(queryFn: (collection: CollectionReference, qb: QueryBuilder) => Query): {
        data: T[];
        unsub: () => void
    } {
        const result = { data: [] as T[] }
        const qb = this.createQueryBuilder()
        const queryRef = queryFn(this.collectionRef, qb)
        const unsub = onSnapshot(queryRef, (snapshot) => {
            result.data = snapshot.docs.map(docSnap => this.mapper.fromFirestore(docSnap))
        })
        return { ...result, unsub }
    }

    public get queryableCollection(): CollectionReference {
        return this.collectionRef
    }

    protected createQueryBuilder(): QueryBuilder {
        const constraints: QueryConstraint[] = []
        const clientQueryRef = this.collectionRef

        return {
            where(field: string, op: WhereOperation, value: unknown) {
                constraints.push(where(field, op, value))
                return this
            },
            orderBy(field: string, direction: 'asc' | 'desc') {
                constraints.push(orderBy(field, direction))
                return this
            },
            limit(n: number) {
                constraints.push(limit(n))
                return this
            },
            build() {
                return query(clientQueryRef, ...constraints)
            },
        }
    }
} 
